/**
 * Accessibility helper component that will visually hide content from user,
 * but still keep it in the DOM for assistive agents to access
 */
import * as React from "react"
import styled from "@oui/system"

type DefaultProps = typeof defaultProps
type Props = Readonly<
  {
    children: React.ReactChild | React.ReactChild[] | React.ReactFragment
    id?: string
  } & DefaultProps
>

const defaultProps = {
  /**
   * The underlying DOM element.
   * @default "span"
   */
  from: "span" as keyof JSX.IntrinsicElements | React.ComponentType<any>
}

const ScreenReaderOnly_ = ({
  from: Component,
  forwardedRef,
  id,
  ...rest
}: Props & { forwardedRef: React.Ref<HTMLElement> }) => {
  return <Component {...rest} id={id} ref={forwardedRef} />
}
ScreenReaderOnly_.defaultProps = defaultProps

const ForwardedScreenReaderOnly_ = (
  props: Omit<Props, keyof DefaultProps> & Partial<DefaultProps>,
  ref: React.Ref<HTMLElement>
) => <ScreenReaderOnly_ {...props} forwardedRef={ref} />
ForwardedScreenReaderOnly_.displayName = "forwarded(ScreenReaderOnly)"

const ForwardedScreenReaderOnly = React.forwardRef(ForwardedScreenReaderOnly_)

const ScreenReaderOnly = styled(ForwardedScreenReaderOnly)({
  position: "absolute",
  clip: "rect(1px 1px 1px 1px)",
  clipPath: "inset(50%)",
  padding: 0,
  border: 0,
  height: "1px",
  width: "1px",
  overflow: "hidden",
  whiteSpace: "nowrap"
})
ScreenReaderOnly.defaultProps = defaultProps

export { ForwardedScreenReaderOnly as _PropsComponent_ }
export default ScreenReaderOnly
